import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Header from "../Header";
import Footer from "../Footer";
import useSeo from "../../hooks/useSeo";
import { assetUrl } from "../../utils/api";
import { secondhandGet, secondhandPost } from "../../api/secondhandApi";
import GoodsChannelTabs from "../shop/GoodsChannelTabs";

export default function SecondhandDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const userId = Number(localStorage.getItem("user_id") || 0);

  const [item, setItem] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [contacting, setContacting] = useState(false);

  useSeo({
    title: item ? `${item.title} - 闲置交换` : "闲置详情",
    metaDescription: item?.description?.slice(0, 120) || "会员闲置详情",
    path: `/secondhand/${id}`,
  });

  useEffect(() => {
    setLoading(true);
    const q = userId ? `?viewer_user_id=${userId}` : "";
    secondhandGet(`/listings/${id}${q}`)
      .then((data) => setItem(data.item))
      .catch((e) => setError(e.message || "加载失败"))
      .finally(() => setLoading(false));
  }, [id, userId]);

  const startChat = async () => {
    if (!userId) {
      navigate(`/login?redirect=${encodeURIComponent(`/secondhand/${id}`)}`);
      return;
    }
    if (!item) return;
    if (item.user_id === userId) {
      alert("这是你自己的闲置");
      return;
    }
    setContacting(true);
    try {
      const data = await secondhandPost(`/listings/${item.id}/chat-grant`, {
        user_id: userId,
      });
      navigate("/chat", {
        state: {
          user: data.seller,
          fromSecondhand: true,
          secondhandListingId: item.id,
          presetMessage: data.preset_message,
        },
      });
    } catch (e) {
      alert(e.message || "无法发起私信");
    } finally {
      setContacting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-amber-50 flex flex-col">
      <Header />
      <main className="flex-1 px-4 py-8">
        <div className="max-w-3xl mx-auto space-y-6">
          <div className="text-sm text-gray-500">
            <Link to="/secondhand" className="text-pink-600 hover:underline">
              闲置交换
            </Link>
            <span className="mx-2">/</span>
            <span>详情</span>
          </div>
          <GoodsChannelTabs />

          {loading ? (
            <div className="text-center text-gray-400 py-20">加载中...</div>
          ) : error || !item ? (
            <div className="bg-white rounded-2xl p-10 text-center text-gray-500">{error || "未找到"}</div>
          ) : (
            <article className="bg-white rounded-2xl border border-pink-100 overflow-hidden shadow-sm">
              {item.images?.length ? (
                <div className="grid grid-cols-2 gap-1 bg-rose-50">
                  {item.images.map((url) => (
                    <img
                      key={url}
                      src={assetUrl(url)}
                      alt=""
                      className="w-full aspect-square object-cover"
                    />
                  ))}
                </div>
              ) : (
                <div className="h-48 bg-rose-50 flex items-center justify-center text-gray-300">暂无图片</div>
              )}

              <div className="p-6 md:p-8 space-y-4">
                <div className="flex flex-wrap gap-2 text-xs">
                  <span className="px-2.5 py-1 rounded-full bg-pink-50 text-pink-600">{item.category_label}</span>
                  <span className="px-2.5 py-1 rounded-full bg-amber-50 text-amber-700">{item.intent_label}</span>
                  <span className="px-2.5 py-1 rounded-full bg-gray-50 text-gray-600">{item.condition_label}</span>
                  <span className="px-2.5 py-1 rounded-full bg-gray-50 text-gray-600">{item.status_label}</span>
                </div>
                <h1 className="text-2xl font-bold text-gray-800">{item.title}</h1>
                {item.price_note && <p className="text-pink-600 font-medium">{item.price_note}</p>}
                <p className="text-sm text-gray-500">
                  {item.location_label || "城市未填"} · {item.seller_name || "会员"}
                </p>
                <div className="text-gray-700 leading-relaxed whitespace-pre-line text-sm">
                  {item.description || "暂无详细说明"}
                </div>
                <p className="text-xs text-gray-400">
                  平台仅提供信息撮合，请线下自行确认物品并注意人身与财物安全。
                </p>

                <div className="flex flex-wrap gap-3 pt-2">
                  {item.user_id === userId ? (
                    <Link
                      to="/secondhand/mine"
                      className="px-6 py-3 rounded-full border border-pink-200 text-pink-600 text-sm font-medium"
                    >
                      管理我的发布
                    </Link>
                  ) : (
                    <button
                      type="button"
                      disabled={contacting || item.status === "done"}
                      onClick={startChat}
                      className="px-6 py-3 rounded-full bg-rose-600 text-white text-sm font-medium disabled:opacity-50"
                    >
                      {contacting ? "连接中..." : "私信 TA"}
                    </button>
                  )}
                  <Link
                    to={`/user/${item.user_id}`}
                    className="px-6 py-3 rounded-full border border-gray-200 text-gray-600 text-sm"
                  >
                    查看主页
                  </Link>
                </div>
              </div>
            </article>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
